import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { getApiBaseUrlAsync } from '../config/apiConfig';

const ConnectionMonitorContext = createContext({
  isServerOnline: true,
  // Çevrimdışı mod: uygulama çalışmaya devam eder, yalnızca ortak çalışma salt okunurdur.
  isOfflineMode: false,
  lastCheckedAt: null,
});

export const useConnectionMonitor = () => {
  const context = useContext(ConnectionMonitorContext);
  if (!context) {
    throw new Error('useConnectionMonitor must be used within ConnectionMonitorProvider');
  }
  return context;
};

const DEFAULT_PING_INTERVAL_MS = 60 * 1000;
const DEFAULT_REQUEST_TIMEOUT_MS = 8000;

const buildHealthCheckUrl = (apiBaseUrl) => {
  if (!apiBaseUrl) return null;

  // Ensure base URL does not have trailing slash
  const normalized = apiBaseUrl.replace(/\/+$/, '');
  // Use /health (server-side) as primary endpoint.
  // Strip `/api` if present to match server health endpoint.
  const baseWithoutApi = normalized.replace(/\/api$/, '');
  return {
    primary: `${baseWithoutApi}/health`,
    fallback: `${normalized}/health-check`,
  };
};

const performHealthPing = async (url, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      },
      mode: 'cors',
    });

    if (response.ok) {
      return true;
    }

    // Treat 404 as a sign that endpoint doesn't exist (fallback)
    if (response.status === 404) {
      return null;
    }

    // Any other non-2xx response is treated as server unreachable
    return false;
  } catch (error) {
    // Abort or network errors
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const ConnectionMonitorProvider = ({ children }) => {
  const [isServerOnline, setIsServerOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const intervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const isServerOnlineRef = useRef(isServerOnline);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let urls = null;

    const setOnlineState = (online) => {
      if (!isMountedRef.current) return;
      const prev = isServerOnlineRef.current;
      isServerOnlineRef.current = online;
      setIsServerOnline(online);
      setLastCheckedAt(new Date());
      // Notify Auth/Socket layers without a second health poller
      if (typeof window !== 'undefined' && prev !== online) {
        window.dispatchEvent(
          new CustomEvent(online ? 'notesplus-server-online' : 'notesplus-server-offline', {
            detail: { at: Date.now() },
          })
        );
      }
    };

    const scheduleRetryCheck = (delayMs) => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        doCheck();
      }, delayMs);
    };

    const doCheck = async () => {
      // If there's already a retry timer scheduled (from a recent failure), cancel it
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      try {
        // navigator.onLine false ise hemen offline kabul et
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          setOnlineState(false);
          // Keep retrying to detect restored connection quickly
          scheduleRetryCheck(5000);
          return;
        }

        if (!urls) {
          const apiBaseUrl = await getApiBaseUrlAsync();
          urls = buildHealthCheckUrl(apiBaseUrl);
          if (!urls) {
            return;
          }
        }

        // Try primary endpoint first (/health)
        let online = await performHealthPing(urls.primary, DEFAULT_REQUEST_TIMEOUT_MS);

        // If the health endpoint doesn't exist, try fallback
        if (online === null && urls.fallback) {
          online = await performHealthPing(urls.fallback, DEFAULT_REQUEST_TIMEOUT_MS);
        }

        setOnlineState(online === true);

        if (online !== true) {
          // If ping failed, try again sooner than the full interval
          scheduleRetryCheck(5000);
        }
      } catch (error) {
        setOnlineState(false);
        scheduleRetryCheck(5000);
      }
    };

    // Offline/online events from the browser
    const handleOnline = () => {
      doCheck();
    };

    const handleOffline = () => {
      setOnlineState(false);
      // When offline is detected via browser event, still keep polling to spot when we come back
      scheduleRetryCheck(5000);
    };

    // Network error events from cloudApi
    const handleNetworkError = () => {
      if (!isMountedRef.current) return;
      if (isServerOnlineRef.current) {
        setOnlineState(false);
      }
      scheduleRetryCheck(5000);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      window.addEventListener('notesplus-network-error', handleNetworkError);
    }

    // Initial check
    doCheck();

    // Periodic check (longer interval to avoid wasting requests)
    intervalRef.current = setInterval(() => {
      doCheck();
    }, DEFAULT_PING_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        window.removeEventListener('notesplus-network-error', handleNetworkError);
      }
    };
  }, []);

  return (
    <ConnectionMonitorContext.Provider
      value={{
        isServerOnline,
        isOfflineMode: !isServerOnline,
        lastCheckedAt,
      }}
    >
      {children}
    </ConnectionMonitorContext.Provider>
  );
};
